import { CloudDownload, Delete, Visibility } from "@material-ui/icons";
import { useKeycloak } from "@react-keycloak/web";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import Button from "components/Forms/CustomButtons/ButtonTS";
import Table from "components/Tables/ReactTable/ReactTable";
import UploadDocumentModal from "components/Modals/UploadDocumentModal";
import React, { useEffect, useRef } from "react";
import { ActivityItemConfig } from "services/input-core/activity-items/@types";
import { useActivityItemConfig } from "services/input-core/activity-items/get-activity-item-config";
import { DocumentAttachment } from "services/input-core/document-attachment/@types";
import { useDeleteDocumentAttachment } from "services/input-core/document-attachment/delete-document-attachment";
import { useDownloadAllAttachmentsFromActivity } from "services/input-core/document-attachment/download-all-attachments-from- activity";
import { useFinishAttachmentUpload } from "services/input-core/document-attachment/finish-attachment-upload";
import { ActivityItemValidationStatusEnum } from "utils/@types";
import {
  checkIfStringInArrayIgnoreCaseAndRemoveSpecialChars,
  checkMessageErrors,
  compareStringsIgnoreCaseAndRemoveSpecialChars,
} from "utils/functions";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import ConfirmUploadComplete from "../ConfirmUploadComplete";
import { useGetPaginatedDocumentAttachments } from "services/input-core/document-attachment/get-paginated-document-attachments";
import { useTranslation } from "react-i18next";
import CustomPopover, {
  CustomPopoverRef,
} from "components/UI/CustomPopover/CustomPopover";
import PopoverButton from "components/UI/CustomPopover/PopoverButton";
import { toast } from "react-toastify";
import { useSubscription } from "store/auth/auth-store";
import ExpirationDateModal from "components/Modals/ExpirationDateModal";
import { parseISO, format } from "date-fns";
import { useFinishAttachmentDownload } from "services/input-core/document-attachment/finish-attachment-download";
import { useBlobToken } from "services/blob/get-token";
import { useActivityItemContext } from "store/activityItem";

interface UploadModalProps {
  profile: "WORKER" | "SOW";
  modalOpen: boolean;
  uploadType?: boolean;
  setUploadType?: React.Dispatch<React.SetStateAction<boolean>>;
  fieldglassId?: string;
  onClose: () => void;
  entityDate?: {
    start: string;
    end: string;
  };
}

async function openImageInNewTab(
  fileUri: string,
  doc: DocumentAttachment,
  notificationCallback: () => void,
) {
  if (!fileUri) return;

  if (fileUri) {
    notificationCallback();
    const { data } = await axios.get(fileUri, {
      responseType: "blob",
    });

    const metadata = {
      type: doc?.mimeType,
      disposition: "inline",
    };

    const file = new File([data], "file", metadata);

    const fileURL = URL.createObjectURL(file);

    window.open(fileURL);
  }
}

const ActionButtons: React.FC<{
  fileUri: string;
  currentFile?: unknown;
  doc?: DocumentAttachment;
  disabled?: boolean;
  disableDelete: boolean;
  handleDelete: () => void;
  notificationCallback: () => void;
  key: number;
}> = ({
  notificationCallback,
  fileUri,
  disableDelete,
  handleDelete,
  doc,
  key,
}) => {
  const { t } = useTranslation("modals");
  const { mutate: blobToken, isLoading: isLoadingBlobToken } = useBlobToken();

  const customPopoverRef = useRef<CustomPopoverRef>(null);

  const closePopoverFromParent = () => {
    if (customPopoverRef.current) {
      customPopoverRef.current.closePopover();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        justifyContent: "flex-end",
        alignItems: "flex-end",
      }}
    >
      <CustomPopover
        ref={customPopoverRef}
        identifier={`upload-content`}
        data-cy={`upload-content-popover-${key}`}
      >
        <PopoverButton
          disabled={!fileUri || isLoadingBlobToken}
          color="primary"
          data-cy={`preview-button`}
          onClick={() => {
            closePopoverFromParent();
            blobToken(fileUri, {
              onSuccess: () => {
                openImageInNewTab(doc?.uri, doc, notificationCallback);
              },
              onError: () => {
                toast.error(t("get-blob-token-error", { ns: "list-errors" }));
              },
            });
          }}
        >
          <Visibility />
          {t("preview")}
        </PopoverButton>
        <PopoverButton
          disabled={!fileUri || isLoadingBlobToken}
          color="primary"
          data-cy={`download-button`}
          onClick={() => {
            closePopoverFromParent();
            blobToken(fileUri, {
              onSuccess: () => {
                window.open(doc?.uri, "_blank", "noopener,noreferrer");
              },
              onError: () => {
                toast.error(t("get-blob-token-error", { ns: "list-errors" }));
              },
            });
          }}
        >
          <CloudDownload />
          {t("download")}
        </PopoverButton>

        {!disableDelete && (
          <PopoverButton
            disabled={disableDelete}
            data-cy={`delete-button`}
            onClick={() => {
              closePopoverFromParent();
              handleDelete();
            }}
          >
            <Delete style={{ fontSize: "1.2rem" }} />
            {t("delete-document")}
          </PopoverButton>
        )}
      </CustomPopover>
    </div>
  );
};

export default function UploadModalContent({
  onClose,
  profile,
  fieldglassId,
  uploadType,
  setUploadType,
  entityDate,
}: UploadModalProps) {
  const { t } = useTranslation(["modals", "list-errors", "list-messages"]);

  const { currentActivityItem } = useActivityItemContext((state) => ({
    currentActivityItem: state.currentActivity,
  }));

  const columns: {
    Header: string;
    accessor: string;
  }[] = [
    {
      Header: t("react-table-column-name"),
      accessor: "name",
    },
    {
      Header: t("react-table-column-uploaded-by"),
      accessor: "uploadedBy",
    },
    {
      Header: t("react-table-column-uploaded-at"),
      accessor: "uploadedAt",
    },
    {
      Header: t("react-table-column-actions"),
      accessor: "actions",
    },
  ];

  const { keycloak } = useKeycloak();
  const queryClient = useQueryClient();

  const { subscription } = useSubscription();

  const refId = fieldglassId ?? currentActivityItem.fieldglassId;

  const [
    currentConfigForActivityItem,
    setCurrentConfigForActivityItem,
  ] = React.useState<ActivityItemConfig>();

  const [uploadModal, setUploadModal] = React.useState(false);
  const [completeModal, setCompleteModal] = React.useState(false);
  const [deleteModal, setDeleteModal] = React.useState(false);
  const [selectedForDelete, setSelectedForDelete] = React.useState<{
    id: string;
    name: string;
  }>(null);
  const [expirationDateModalOpen, setExpirationDateModalOpen] = React.useState(
    false,
  );

  const [onUploadSuccessValue, setOnUploadSuccessValue] = React.useState(false);
  const [downloadAllClicked, setDownloadAllClicked] = React.useState(false);

  const isWaitingDownload = () => {
    if (
      compareStringsIgnoreCaseAndRemoveSpecialChars(
        currentActivityItem?.validationStatus,
        ActivityItemValidationStatusEnum.WAITING_FOR_DOWNLOAD,
      ) ||
      compareStringsIgnoreCaseAndRemoveSpecialChars(
        currentActivityItem?.validationStatus,
        ActivityItemValidationStatusEnum.SUPPLIER_DOWNLOAD,
      )
    ) {
      const roles =
        currentConfigForActivityItem?.validationSteps[
          currentActivityItem?.validationStep
        ]?.executedByRole;

      //Se só tem o supplier como role, então a conta não pode ser buyer
      if (roles?.length === 1 && roles?.includes("supplier")) {
        return (
          keycloak.hasResourceRole("supplier", "uverify") &&
          !keycloak.hasResourceRole("buyer", "uverify")
        );
      }

      let hasRole = false;

      roles?.forEach((role) => {
        hasRole = hasRole || keycloak.hasResourceRole(role, "uverify");
      });

      return hasRole;
    }

    return false;
  };

  const {
    data: documentsFromActivity,
    refetch: refetchDocumentsFromActivity,
  } = useGetPaginatedDocumentAttachments(
    {
      active: true,
      zID: currentActivityItem?.zIdentifier,
      fieldglassId: currentActivityItem?.fieldglassId || fieldglassId,
      params: {
        limit: 10,
        page: 1,
      },
    },
    { enabled: (isWaitingDownload() || onUploadSuccessValue) && !uploadType },
  );

  const {
    mutate: deleteAttachment,
    isLoading: isDeletingAttachment,
  } = useDeleteDocumentAttachment();

  const {
    mutate: downloadAllAttachments,
    isLoading: isDownloadingAllAttachments,
  } = useDownloadAllAttachmentsFromActivity();

  const {
    mutate: finishUploadAttachments,
    isLoading: isFinishingUploadAttachments,
  } = useFinishAttachmentUpload();

  const {
    mutate: finishDownloadAttachments,
    isLoading: isFinishDownloadAttachments,
  } = useFinishAttachmentDownload();

  const { data: activityItemConfig } = useActivityItemConfig();

  useEffect(() => {
    if (activityItemConfig)
      setCurrentConfigForActivityItem(
        activityItemConfig.find(
          (element) =>
            element.mapping === currentActivityItem?.activityItemCode,
        ),
      );
  }, [activityItemConfig]);

  const isWaitingUpload = () => {
    if (!currentActivityItem?.validationStep) return true;

    if (
      currentActivityItem?.validationStep &&
      (currentConfigForActivityItem?.validationSteps[
        currentActivityItem?.validationStep - 1
      ].name === "CHANGE_ACTIVITY_STATUS" ||
        checkIfStringInArrayIgnoreCaseAndRemoveSpecialChars(
          currentActivityItem?.validationStatus,
          [
            ActivityItemValidationStatusEnum.SUPPLIER_DOWNLOADED,
            ActivityItemValidationStatusEnum.WAITING_DOCUMENTS,
            ActivityItemValidationStatusEnum.SUPPLIER_UPLOADING_DOCUMENTS,
          ],
        ))
    ) {
      let roles =
        currentConfigForActivityItem?.validationSteps[
          currentActivityItem?.validationStep - 1
        ]?.executedByRole;

      if (
        compareStringsIgnoreCaseAndRemoveSpecialChars(
          currentActivityItem?.validationStatus,
          ActivityItemValidationStatusEnum.SUPPLIER_DOWNLOADED,
        )
      ) {
        roles = ["supplier"];
      }

      if (roles?.length === 1 && roles?.includes("supplier")) {
        return (
          keycloak.hasResourceRole("supplier", "uverify") &&
          !keycloak.hasResourceRole("buyer", "uverify")
        );
      }

      let hasRole = false;

      roles?.forEach((role) => {
        hasRole = hasRole || keycloak.hasResourceRole(role, "uverify");
      });

      return hasRole;
    }
    return false;
  };

  const dataFormatted =
    documentsFromActivity?.docs?.map((doc, index) => ({
      name: doc.name,
      uploadedBy: `${doc?.createdBy?.name} (${doc?.createdBy?.email})`,
      uploadedAt: new Date(doc.createdAt).toLocaleString(),
      fromApi: true,
      actions: (
        <ActionButtons
          fileUri={doc.uri}
          doc={doc}
          key={index}
          disableDelete={isWaitingDownload()}
          notificationCallback={() => toast.info(t("opening-preview"))}
          handleDelete={() => {
            setSelectedForDelete({
              id: doc._id,
              name: doc.name,
            });

            setDeleteModal(true);
          }}
        />
      ),
    })) || [];

  function download(content: string) {
    const a = document.createElement("a");
    a.href = content;
    a.target = "_blank";
    a.download = content;
    a.setAttribute("type", "hidden");

    document.body.appendChild(a);

    a.click();
    a.remove();
  }

  const handleDelete = (attachmentId: string) => {
    deleteAttachment(attachmentId, {
      onSuccess: () => {
        toast.success(t("attachment-deleted-successfully"));
        refetchDocumentsFromActivity();
        setDeleteModal(false);
      },
      onError: (error: any) => {
        toast.error(
          t(
            checkMessageErrors(
              error?.response?.data?.error,
              "ERR_DELETING_ATTACHMENT_DEFAULT",
            ),
            { ns: "list-errors" },
          ),
        );
      },
    });
  };

  const handleDownloadAll = () => {
    downloadAllAttachments(
      {
        activityItemCode: currentActivityItem?.activityItemCode,
        fieldglassId: refId,
        subscriptionId: subscription?.subscriptionId,
      },
      {
        onSuccess: (data) => {
          toast.success(t("download-successfully"));
          queryClient.invalidateQueries([
            profile == "SOW"
              ? "activity-items-sow"
              : profile == "WORKER" && "activity-items-worker",
            refId,
          ]);

          download(data);
          setDownloadAllClicked(true);
        },
        onError: (error: any) => {
          toast.error(
            t(
              checkMessageErrors(
                error?.response?.data?.error,
                "ERR_DOWNLOAD_FAILED_DEFAULT",
              ),
              { ns: "list-errors" },
            ),
          );
        },
      },
    );
  };

  const handleFinishAttachments = (
    expirationDate?: Date,
    validationExpirationStartDate?: Date,
  ) => {
    toast.info(t("info-finishing-upload", { ns: "list-messages" }));

    const payload = {
      zIdentifier: currentActivityItem?.zIdentifier,
      activityItemCode: currentActivityItem?.activityItemCode,
      fieldglassId: refId,
      subscriptionId: currentActivityItem?.subscription,
    };

    finishUploadAttachments(
      currentActivityItem?.hasExpirationDate && expirationDate
        ? {
            ...payload,
            expirationDate: format(expirationDate, "yyyy-MM-dd"),
            ...(currentActivityItem?.hasStartDate && {
              validationExpirationStartDate,
            }),
          }
        : payload,
      {
        onSuccess: () => {
          toast.success(t("success", { ns: "list-messages" }));
          queryClient.invalidateQueries([
            profile == "SOW"
              ? "activity-items-sow"
              : profile == "WORKER" && "activity-items-worker",
            refId,
          ]);
          onClose();
        },
        onError: (error: any) => {
          toast.error(
            t(
              checkMessageErrors(
                error?.response?.data?.error,
                "ERR_SAVE_DEFAULT",
              ),
              { ns: "list-errors" },
            ),
          );
        },
      },
    );
  };

  const handleFinishDownload = () => {
    toast.info(t("info-download-upload", { ns: "list-messages" }));

    finishDownloadAttachments(
      {
        zIdentifier: currentActivityItem?.zIdentifier,
        activityItemCode: currentActivityItem?.activityItemCode,
        fieldglassId: refId,
        subscriptionId: currentActivityItem?.subscription,
      },
      {
        onSuccess: () => {
          toast.success(t("download-step-completed")),
            queryClient.invalidateQueries([
              profile == "SOW"
                ? "activity-items-sow"
                : profile == "WORKER" && "activity-items-worker",
              refId,
            ]);
          onClose();
        },
        onError: (error: any) => {
          toast.error(
            t(
              checkMessageErrors(
                error?.response?.data?.error,
                "ERR_DOWNLOAD_STEP_FAILED_DEFAULT",
              ),
              { ns: "list-errors" },
            ),
          );
        },
      },
    );
  };

  const isButtonsDisabled =
    isDeletingAttachment ||
    isDownloadingAllAttachments ||
    isFinishingUploadAttachments ||
    isFinishDownloadAttachments;

  const onUploadSuccess = (value: any) => {
    setOnUploadSuccessValue(value);
  };

  return (
    <>
      <ConfirmDeleteModal
        isDeleting={isDeletingAttachment}
        modalOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={() => handleDelete(selectedForDelete?.id)}
        fileName={selectedForDelete?.name}
      />

      <ConfirmUploadComplete
        isUploading={isFinishingUploadAttachments}
        modalOpen={completeModal}
        onClose={() => setCompleteModal(false)}
        onConfirm={() =>
          currentActivityItem?.hasExpirationDate
            ? setExpirationDateModalOpen(true)
            : handleFinishAttachments()
        }
      />

      <UploadDocumentModal
        modalOpen={uploadModal}
        onClose={() => {
          setUploadModal(false);
          refetchDocumentsFromActivity();
        }}
        profile={profile}
        fieldglassId={fieldglassId ?? refId}
        onUploadSuccess={onUploadSuccess}
        setUploadType={setUploadType}
      />

      <ExpirationDateModal
        modalOpen={expirationDateModalOpen}
        onClose={() => setExpirationDateModalOpen(false)}
        onConfirm={handleFinishAttachments}
        isUploading={isFinishingUploadAttachments}
        entityDate={entityDate}
        notPreSetDates={true}
      />

      <Table
        columns={columns}
        data={dataFormatted}
        initialPageSize={5}
        columnsNotTrimFilters={["name", "uploadedBy"]}
      />

      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "flex-end",
          gap: "8px",
          marginTop: "24px",
          marginBottom: "24px",
        }}
      >
        {isWaitingUpload() && (
          <>
            <Button
              color="primary"
              data-cy={`add-file-modal-button`}
              onClick={() => {
                setUploadModal(true);
              }}
              disabled={isButtonsDisabled || isWaitingDownload()}
            >
              {t("add-files")}
            </Button>

            <Button
              color="primary"
              onClick={() => setCompleteModal(true)}
              disabled={
                isButtonsDisabled ||
                isWaitingDownload() ||
                dataFormatted.length === 0
              }
            >
              {t("complete-upload")}
            </Button>
          </>
        )}

        {isWaitingDownload() && (
          <Button
            color="info"
            onClick={() => handleDownloadAll()}
            disabled={isButtonsDisabled}
          >
            {t("download-all")}
          </Button>
        )}

        {isWaitingDownload() && (
          <Button
            color="success"
            onClick={() => handleFinishDownload()}
            disabled={isButtonsDisabled || !downloadAllClicked}
          >
            {t("complete-download")}
          </Button>
        )}
      </div>
    </>
  );
}
